// src/authAprRequest.js

import axios from "axios";
import { ERROR_MESSAGES } from "./errorMessages";
import { waitForStableOnline } from "./useOnlineStatus";
import { captureCameBackOnline, captureWentOffline } from "./networkTelemetry";
import {
  isOfflineError,
  isTimeoutError,
  isUnauthorizedError,
  isForbiddenError,
  isServerError,
  redirectToLogin,
} from "./requestErrorUtils";

const MAX_GET_RETRY_ATTEMPTS = 1;
const GET_RETRY_DELAY_MS = 1200;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isTransientGetError = (error) => {
  if (isTimeoutError(error) || isOfflineError(error)) {
    return true;
  }

  // Treat "no response" as transient for GETs (transport/edge hiccups).
  return !error?.response;
};

export async function authApiRequest(
  context,
  endpoint,
  method,
  data,
  options = {},
) {
  const { authReady, getToken, setToken, getOnError } = context;
  const {
    skipGetRetry = false,
    skipWaitForStableOnline = false,
    ...requestOptions
  } = options;

  await authReady;

  const normalizedMethod = method.toLowerCase();
  const isGet = normalizedMethod === "get";
  const startedOffline = !navigator.onLine;
  const onError = getOnError?.();

  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  const token = getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else {
    console.log("NOTLOGGEDIN");
    setToken("");
    return {};
  }

  if (startedOffline && onError && !options.skipGlobalErrorHandler) {
    onError({
      message: isGet ? ERROR_MESSAGES.OFFLINE_GET : ERROR_MESSAGES.OFFLINE_POST,
      persist: true,
      showButtons: isGet,
      source: isGet ? "get" : "post",
    });
  }

  if (startedOffline && !isGet) {
    const offlineBlockedError = new Error("Blocked offline non-GET request");
    offlineBlockedError.isOfflineBlocked = true;
    throw offlineBlockedError;
  }

  if (!skipWaitForStableOnline) {
    await waitForStableOnline(startedOffline && isGet);
  }

  let retryAttempt = 0;

  while (true) {
    try {
      let result;
      const axiosOptions = {
        ...requestOptions,
        _retryAttempt: retryAttempt,
      };

      if (normalizedMethod === "post") {
        result = await axios.post(endpoint, data, {
          headers,
          ...axiosOptions,
        });
      } else if (normalizedMethod === "get") {
        result = await axios.get(endpoint, {
          headers,
          params: data,
          ...axiosOptions,
        });
      } else {
        result = await axios({
          url: endpoint,
          method,
          headers,
          data,
          ...axiosOptions,
        });
      }

      if (startedOffline && onError) {
        captureCameBackOnline({
          source: "auth_api",
          reason: "reconnect",
        });
        onError(null);
      }

      return result;
    } catch (e) {
      const canRetryGet =
        isGet &&
        !skipGetRetry &&
        retryAttempt < MAX_GET_RETRY_ATTEMPTS &&
        isTransientGetError(e);

      if (canRetryGet) {
        retryAttempt += 1;

        if (!navigator.onLine && !skipWaitForStableOnline) {
          await waitForStableOnline(true);
        }

        await delay(GET_RETRY_DELAY_MS * retryAttempt);
        continue;
      }

      const errorMsgOffline = {
        message: ERROR_MESSAGES.OFFLINE_GET,
        showButtons: true,
        persist: true,
        source: "get",
      };

      if (isGet && !navigator.onLine) {
        captureWentOffline({
          source: "auth_api",
          api_url: endpoint,
          api_method: normalizedMethod.toUpperCase(),
          reason: "offline",
        });
        if (onError && !options.skipGlobalErrorHandler) {
          onError(errorMsgOffline);
        }
        return;
      }

      if (isUnauthorizedError(e)) {
        redirectToLogin(setToken);
        throw e;
      }

      if (onError && !options.skipGlobalErrorHandler) {
        if (!navigator.onLine) {
          onError({
            message: isGet
              ? ERROR_MESSAGES.OFFLINE_GET
              : ERROR_MESSAGES.OFFLINE_POST,
            persist: true,
            showButtons: isGet,
            source: isGet ? "get" : "post",
          });
        } else if (isTimeoutError(e)) {
          onError({
            message: ERROR_MESSAGES.TIMEOUT,
            showButtons: isGet,
            showRetry: isGet,
          });
        } else if (isOfflineError(e)) {
          onError({
            message: ERROR_MESSAGES.NETWORK_UNREACHABLE,
            showButtons: isGet,
          });
        } else if (isServerError(e)) {
          onError({
            message: ERROR_MESSAGES.SERVER_GENERIC,
            showButtons: isGet,
          });
        } else if (isForbiddenError(e)) {
          onError({
            message: ERROR_MESSAGES.PERMISSION,
            showButtons: false,
          });
        } else {
          onError({
            message: isGet
              ? ERROR_MESSAGES.LOAD_FAILED
              : ERROR_MESSAGES.SAVE_FAILED,
            showButtons: false,
          });
        }
      }

      throw e;
    }
  }
}
